/**
 * Org SKU / unit serial convention — pattern + placeholders.
 * Production pipeline will later supply real serial digits + build date.
 *
 * Token helpers are inlined (not imported from composePlan) so this module
 * stays importable from both Vite app and NodeNext server typecheck.
 */

function sizeCompact(sizeLabel: string): string {
  return String(sizeLabel ?? "").replace(/[x×X]/g, "").trim();
}

function styleAbbrev(styleKey: string): string {
  const raw = String(styleKey ?? "").trim();
  if (!raw) return "";
  const m = raw.match(/^(.+?)\s*[=|]\s*([A-Za-z0-9]{1,4})$/);
  if (m?.[2]) return m[2].toUpperCase();
  return raw.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase();
}

function styleDisplayKey(styleKey: string): string {
  const raw = String(styleKey ?? "").trim();
  const m = raw.match(/^(.+?)\s*[=|]\s*[A-Za-z0-9]{1,4}$/);
  return (m?.[1] ?? raw).trim();
}

export type SkuSerialConvention = {
  /** e.g. {SIZE_COMPACT}-{GROUP}-{STYLE_ABBREV}-{PACKAGE}-{SERIAL}-{DATE} */
  pattern: string;
  /** Width for {SERIAL} placeholder / future counter. */
  serial_digits: number;
  /** Token format for {DATE}: ddmmyy, yymmdd, yyyymmdd, … */
  date_format: string;
  /** Default for {PACKAGE} when no package modifier (e.g. NP). */
  default_package: string;
};

export const DEFAULT_SKU_SERIAL_CONVENTION: SkuSerialConvention = {
  pattern: "{SIZE_COMPACT}-{GROUP}-{STYLE_ABBREV}-{PACKAGE}-{SERIAL}-{DATE}",
  serial_digits: 5,
  date_format: "ddmmyy",
  default_package: "NP",
};

export type SerialConventionSample = {
  sizeLabel?: string;
  groupCode?: string;
  seriesName?: string;
  styleKey?: string;
  packageCode?: string;
  serialNumber?: number | null;
  /** When null/undefined, {DATE} renders a soft placeholder from date_format letters. */
  productionDate?: Date | null;
};

export function formatConventionDate(date: Date, format: string): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  const yy = yyyy.slice(-2);
  const fmt = (format || "ddmmyy").trim() || "ddmmyy";
  return fmt
    .replace(/yyyy/gi, yyyy)
    .replace(/yy/gi, yy)
    .replace(/mm/gi, mm)
    .replace(/dd/gi, dd);
}

export function formatSerialDigits(
  serialNumber: number | null | undefined,
  digits: number,
): string {
  const width = Math.min(12, Math.max(1, Math.floor(digits) || 5));
  if (serialNumber == null || !Number.isFinite(serialNumber) || serialNumber < 0) {
    return "#".repeat(width);
  }
  const n = Math.floor(serialNumber);
  const s = String(n);
  return s.length >= width ? s : "0".repeat(width - s.length) + s;
}

export function normalizeSkuSerialConvention(raw: unknown): SkuSerialConvention {
  const d = DEFAULT_SKU_SERIAL_CONVENTION;
  if (!raw || typeof raw !== "object") return { ...d };
  const r = raw as Record<string, unknown>;
  const serial_digits =
    typeof r.serial_digits === "number" && Number.isFinite(r.serial_digits)
      ? Math.min(12, Math.max(1, Math.floor(r.serial_digits)))
      : d.serial_digits;
  return {
    pattern:
      typeof r.pattern === "string" && r.pattern.trim() ? r.pattern.trim() : d.pattern,
    serial_digits,
    date_format:
      typeof r.date_format === "string" && r.date_format.trim()
        ? r.date_format.trim()
        : d.date_format,
    default_package:
      typeof r.default_package === "string" && r.default_package.trim()
        ? r.default_package.trim().toUpperCase()
        : d.default_package,
  };
}

/**
 * Render a full serial string from org convention + sample context.
 * {SERIAL} / {DATE} stay placeholders until the production pipeline wires them.
 */
export function formatSkuSerial(
  convention: SkuSerialConvention,
  sample: SerialConventionSample = {},
): string {
  const c = normalizeSkuSerialConvention(convention);
  const size = sample.sizeLabel ?? "10x12";
  const group = (sample.groupCode ?? "CL").trim().toUpperCase();
  const styleRaw = sample.styleKey ?? "Utility";
  const style = styleDisplayKey(styleRaw);
  const series = sample.seriesName ?? group;
  const pkg = (sample.packageCode ?? c.default_package).trim().toUpperCase() || "NP";
  const serial = formatSerialDigits(sample.serialNumber, c.serial_digits);
  const date =
    sample.productionDate != null
      ? formatConventionDate(sample.productionDate, c.date_format)
      : c.date_format.replace(/[A-Za-z]/g, "#");

  return c.pattern
    .replace(/\{SIZE_COMPACT\}/gi, sizeCompact(size))
    .replace(/\{SIZENOX\}/gi, sizeCompact(size))
    .replace(/\{STYLE_ABBREV\}/gi, styleAbbrev(styleRaw))
    .replace(/\{PACKAGE\}/gi, pkg)
    .replace(/\{SERIAL\}/gi, serial)
    .replace(/\{DATE\}/gi, date)
    .replace(/\{GROUP\}/gi, group)
    .replace(/\{CODE\}/gi, group)
    .replace(/\{SERIES\}/gi, series)
    .replace(/\{SIZE\}/gi, size)
    .replace(/\{STYLE\}/gi, style)
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export const SERIAL_CONVENTION_TOKEN_HELP =
  "{SIZE_COMPACT} {GROUP}/{CODE} {STYLE_ABBREV} {PACKAGE} {SERIAL} {DATE} — also {SIZE} {SERIES} {STYLE}";

const SIZE_TOKENS = new Set(["SIZE", "SIZE_COMPACT", "SIZENOX"]);

export type DecodedSerialPart = {
  token: string;
  value: string;
};

export type DecodedSkuSerial = {
  matched: boolean;
  parts: DecodedSerialPart[];
  size: string;
  serial: string;
};

/** 1428 → 14x28, 812 → 8x12, 1016 → 10x16. */
export function expandSizeCompact(compact: string): string {
  const digits = compact.replace(/\D/g, "");
  if (digits.length < 3) return compact.trim();
  for (const width of ["16", "14", "12", "10", "8"]) {
    if (!digits.startsWith(width) || digits.length <= width.length) continue;
    const length = digits.slice(width.length).replace(/^0+/, "") || "0";
    return `${width}x${length}`;
  }
  const mid = Math.ceil(digits.length / 2);
  return `${digits.slice(0, mid).replace(/^0+/, "") || "0"}x${digits.slice(mid).replace(/^0+/, "") || "0"}`;
}

function patternSlots(pattern: string): Array<{ token: string | null; literal: string | null }> {
  return pattern
    .split("-")
    .map((slot) => slot.trim())
    .filter(Boolean)
    .map((slot) => {
      const token = /^\{([A-Za-z0-9_]+)\}$/.exec(slot);
      if (token?.[1]) return { token: token[1].toUpperCase(), literal: null };
      return { token: null, literal: slot };
    });
}

/**
 * Split an incoming serial by the org pattern.
 * Size and short serial are the fields used today. Other tokens stay on `parts`.
 */
export function decodeSkuSerial(pattern: string, raw: string): DecodedSkuSerial {
  const text = raw.trim();
  const slots = patternSlots(pattern);
  const segments = text.split("-").map((part) => part.trim()).filter(Boolean);
  const aligned = slots.length > 0 && slots.length === segments.length;
  const literalsAgree =
    aligned &&
    slots.every((slot, index) => !slot.literal || slot.literal.toLowerCase() === segments[index]!.toLowerCase());
  const parts: DecodedSerialPart[] = [];
  if (aligned && literalsAgree) {
    slots.forEach((slot, index) => {
      if (!slot.token) return;
      parts.push({ token: slot.token, value: segments[index]! });
    });
  }
  const sizePart = parts.find((part) => SIZE_TOKENS.has(part.token));
  const serialPart = parts.find((part) => part.token === "SERIAL");
  const size = !sizePart
    ? ""
    : sizePart.token === "SIZE" && /x/i.test(sizePart.value)
      ? sizePart.value.toLowerCase().replace(/\s+/g, "")
      : expandSizeCompact(sizePart.value);
  return {
    matched: aligned && literalsAgree && parts.length > 0,
    parts,
    size,
    serial: serialPart?.value ?? "",
  };
}
